import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VendorRow } from "_interfaces/vendor.interfaces";
import { Columns, TableSortState } from "components/table";
import { SelectOption } from "components/select";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { toast } from "react-toastify";
import Button from "components/button";
import Icon from "components/icon";
import ActionMenu from "components/shared/action-menu";
import { useNavigate } from "react-router-dom";
import { editVendorPageRouteName } from "../../constants/routes";
import {
  TransportResourceVendorListItem,
  useTransportResourceServiceV1GetVendorsQuery,
  useTransportResourceServiceV1DeleteVendorDraftMutation,
  useTransportResourceServiceV1BulkUpdateVendorStatusMutation,
  vendorApi
} from "_services/modules/transport-resource/vendor";
import { dateTimeFormatter } from "_helper/timeFormatter";
import { errorHandler } from "_services/errorHandler";

export type { VendorRow } from "_interfaces/vendor.interfaces";

const sortByMap: Record<string, string> = {
  vendorName: "name",
  legalName: "legal_name",
  officialPhone: "phone",
  registeredFleet: "registered_fleet",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

const normalizeStatus = (value?: string): "active" | "inactive" =>
  value === "active" ? "active" : "inactive";

export const mapVendorItemsToOptions = (
  vendors?: TransportResourceVendorListItem[],
): SelectOption[] => {
  if (!vendors) {
    return [];
  }

  return vendors
    .filter((vendor): vendor is TransportResourceVendorListItem & { id: string } => Boolean(vendor.id))
    .map((vendor) => ({
      value: vendor.id,
      label: vendor.name ?? vendor.code ?? vendor.id,
      subtitle: vendor.code,
    }));
};

export const useVendorSelectOptions = (defaultLimit = 5) => {
  const { data: initialData, isFetching: isQueryFetching } =
    useTransportResourceServiceV1GetVendorsQuery({
      page: 1,
      limit: defaultLimit,
      searchBy: "name",
    });

  const [triggerGetVendors, { isFetching: isLazyFetching }] =
    vendorApi.endpoints.transportResourceServiceV1GetVendors.useLazyQuery();

  const searchSeqRef = useRef(0);

  const initialOptions = useMemo(
    () => mapVendorItemsToOptions(initialData?.vendor),
    [initialData],
  );

  const [searchedOptions, setSearchedOptions] = useState<SelectOption[] | null>(null);

  const searchVendors = useCallback(
    async (search: string) => {
      if (!search) {
        setSearchedOptions(null);
        return initialOptions;
      }

      const currentSeq = ++searchSeqRef.current;
      try {
        const response = await triggerGetVendors({
          page: 1,
          limit: defaultLimit,
          search: search || undefined,
          searchBy: "name",
        }).unwrap();

        const options = mapVendorItemsToOptions(response.vendor);
        if (currentSeq === searchSeqRef.current) {
          setSearchedOptions(options);
        }
        return options;
      } catch {
        if (currentSeq === searchSeqRef.current) {
          setSearchedOptions([]);
        }
        return [];
      }
    },
    [defaultLimit, initialOptions, triggerGetVendors],
  );

  const vendorOptions = searchedOptions ?? initialOptions;

  return {
    vendorOptions,
    isLoadingVendorOptions: isQueryFetching || isLazyFetching,
    onSearchVendors: searchVendors,
  };
};

const useVendorList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vendor");

  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<VendorRow | null>(null);

  const {
    data: apiResponse,
    isLoading,
    refetch,
  } = useTransportResourceServiceV1GetVendorsQuery(
    {
      page: currentPage,
      limit: rowsPerPage,
      search: searchValue || undefined,
      status: "Draft",
      sortBy: sortState ? sortByMap[sortState.columnId] : undefined,
      order: sortState?.direction,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );



  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-draft") || "Vendor Drafts",
        hasBackButton: true,
      }),
    );
  }, [dispatch, t]);

  const tableData: VendorRow[] = useMemo(() => {
    if (!apiResponse?.vendor) {
      return [];
    }

    return apiResponse.vendor.map((item) => ({
      id: item.id ?? "",
      vendorName: item.name ?? "-",
      vendorCode: item.code ?? "-",
      legalName: item.legal_name ?? "-",
      officialPhone: item.phone ?? "-",
      registeredFleet: item.registered_fleet ?? 0,
      status: normalizeStatus(item.status),
      createdBy: item.created_by?.name ?? "-",
      createdAt: item.created_by?.timestamp
        ? dateTimeFormatter(item.created_by.timestamp)
        : "-",
      updatedBy:
        item.updated_by?.name || item.created_by?.name || "-",
      updatedAt:
        item.updated_by?.timestamp
          ? dateTimeFormatter(item.updated_by.timestamp)
          : item.created_by?.timestamp
          ? dateTimeFormatter(item.created_by.timestamp)
          : undefined,
    }));
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  const handleActionInProgress = () => {
    toast.info(t("toast-feature-coming"));
  };

  const [toggleStatus] = useTransportResourceServiceV1BulkUpdateVendorStatusMutation();

  const handleToggleStatus = async (row: VendorRow) => {
    try {
      const nextStatus = row.status === "active" ? "inactive" : "active";
      await toggleStatus({
        transportResourceBulkUpdateVendorStatusReq: {
          ids: [row.id],
          status: nextStatus,
        },
      }).unwrap();

      const message = nextStatus === "active"
        ? t("toast-activate-success", { name: row.vendorName })
        : t("toast-deactivate-success", { name: row.vendorName });

      toast.success(message);
      refetch();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleDeleteRequest = (row: VendorRow) => {
    setDeleteRow(row);
    setShowDeleteConfirmModal(true);
  };

  const [deleteVendorDraft, { isLoading: isDeleting }] = useTransportResourceServiceV1DeleteVendorDraftMutation();

  const handleConfirmDelete = async () => {
    if (!deleteRow && selectedKeys.length === 0) {
      return;
    }

    const idsToDelete = deleteRow
      ? [deleteRow.id]
      : (selectedKeys as string[]);

    try {
      await Promise.all(
        idsToDelete.map((id) =>
          deleteVendorDraft({ id }).unwrap(),
        ),
      );

      toast.success(
        t("toast-draft-delete-success") ||
        "Draft deleted successfully",
      );

      dispatch(vendorApi.util.invalidateTags(["Vendor"]));

      refetch();
    } catch (error) {
      toast.error(
        t("toast-delete-failed") ||
        "Failed to delete draft",
      );
    } finally {
      setDeleteRow(null);
      setSelectedKeys([]);
      setShowDeleteConfirmModal(false);
    }
  };

  const columns: Columns<VendorRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
        fixed: "left",
      },
      {
        id: "vendorName",
        fieldId: "vendorName",
        fieldId2: "vendorCode",
        label: t("col-vendor"),
        sortable: true,
      },
      {
        id: "legalName",
        fieldId: "legalName",
        label: t("col-legal-name"),
        sortable: true,
      },
      {
        id: "officialPhone",
        fieldId: "officialPhone",
        label: t("col-official-phone"),
        sortable: true,
      },
      {
        id: "registeredFleet",
        fieldId: "registeredFleet",
        label: t("col-registered-fleet"),
        sortable: true,
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "150px",
        dragDisabled: true,
        fixed: "right",
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => navigate(editVendorPageRouteName(row.id), { state: { from: "draft" } })}
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label:
                    row.status === "active"
                      ? t("btn-deactivate")
                      : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                },
                {
                  label: t("btn-delete"),
                  onClick: () => handleDeleteRequest(row),
                  variant: "danger",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return {
    columns,
    currentPage,
    deleteRow,
    paginatedRows: tableData,
    rowsPerPage,
    searchValue,
    selectedKeys,
    setCurrentPage: (page: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setCurrentPage(page);
    },
    setRowsPerPage: (rows: number | ((prev: number) => number)) => {
      setSelectedKeys([]);
      setRowsPerPage(rows);
    },
    setSearchValue: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setSearchValue(val);
    },
    setSelectedKeys,
    setShowDeleteConfirmModal,
    setSortState: (sort: TableSortState | ((prev: TableSortState) => TableSortState)) => {
      setSelectedKeys([]);
      setSortState(sort);
    },
    setStatusFilter: (val: string | ((prev: string) => string)) => {
      setSelectedKeys([]);
      setStatusFilter(val);
    },
    showDeleteConfirmModal,
    sortState,
    statusFilter,
    t,
    isLoading,
    totalPages,
    totalSelected: selectedKeys.length,
    totalVisibleData:
      apiResponse?.metadata?.total ?? 0,
    isDeleting,
    onConfirmDelete:
      handleConfirmDelete,
    allowedBulkActions: ["Delete"],
    onBulkStatusUpdate: (action: string) => {
      if (action === "Delete") {
        setShowDeleteConfirmModal(true);
      }
    },
    onRowClick: (row: VendorRow) => {
      navigate(editVendorPageRouteName(row.id), { state: { from: "draft" } });
    },
  };
};

export default useVendorList;
